import { styled } from '@mui/material';
import InstructorInvitations from 'components/CourseDashboard/InstructorInvitations';
import MemberFilter from 'components/CourseDashboard/MemberFilter';
import MemberList from 'components/CourseDashboard/MemberList';
import StudentInvitations from 'components/CourseDashboard/StudentInvitations';
import { useAuth } from 'contexts/auth';
import { useSnackbar } from 'contexts/snackbar';
import { useViewport } from 'contexts/viewport';
import { PageContext } from 'pages/Teams/PageProvider';
import { useContext } from 'react';
import { Button, Checkbox, Drawer, Dropdown, Icon, IconButton, Stack, Text, TextInput } from 'shell/components';
import theme from 'shell/themes';
import { getUserPreferredFullName } from 'utils/helper';

const isLTI = process.env.REACT_APP_AUTH === 'LTI';

const MembersTab = ({ course, accountUsers, sendInvitations, removeUser }) => {
    const { auth } = useAuth();
    const { message } = useSnackbar();
    const { device } = useViewport();

    const {
        setDialog,
        checked,
        setChecked,
        isInviteInstructorOpen,
        setIsInviteInstructorOpen,
        inviteInstructorInputValue,
        setInviteInstructorInputValue,
        inviteInstructorEmails,
        setInviteInstructorEmails,
        isInviteInstructorLoading,
        setIsInviteInstructorLoading,
        isInviteStudentOpen,
        setIsInviteStudentOpen,
        inviteStudentAccordions,
        setInviteStudentAccordions,
        isSyncRosterLoading,
        downloadRosterCSV,
        csvStudents,
        setCSVStudents,
        inviteStudentInputValue,
        setInviteStudentInputValue,
        inviteStudentEmails,
        setInviteStudentEmails,
        isInviteStudentLoading,
        setIsInviteStudentLoading,
        setSyncRosterDialog,
        memberFilters,
        setMemberFilters,
        setAccountUsers,
        sortState,
        handleSetSortState,
    } = useContext(PageContext);

    const countFilters = (filterState, sortState) => {
        let count = 0;
        if (sortState.orderBy) count++;
        if (filterState.filterKeys.length) count++;
        return count;
    };

    const handleSelectInvite = ({ key }) => {
        if (key === 'instructors') setIsInviteInstructorOpen(true);
        if (key === 'students') setIsInviteStudentOpen(true);
    };

    const getMemberActionOptions = (accountUser) => {
        const canBeInvited = !Boolean(accountUser.login_activity);
        const canBeRemoved = auth.user_id !== accountUser.user_id;
        return [
            { key: 'copy', value: 'Copy Email' },
            ...(process.env.REACT_APP_AUTH === 'PASSWORD'
                ? [
                      { key: 'invite', value: 'Resend Invitation', disabled: !canBeInvited },
                      { key: 'delete', value: 'Remove', disabled: !canBeRemoved },
                  ]
                : []),
        ];
    };

    const handleSelectMemberAction = async (key, accountUser) => {
        switch (key) {
            case 'copy':
                await navigator.clipboard.writeText(accountUser.email);
                message.success('Email copied successfully.');
                break;
            case 'invite':
                message.success(
                    `${accountUser.role.charAt(0).toUpperCase()}${accountUser.role.slice(1)} successfully reinvited.`
                );
                sendInvitations(accountUser.role, [accountUser]);
                break;
            case 'delete':
                setDialog((current) => ({
                    ...current,
                    open: true,
                    message: 'Remove Member?',
                    description: `Are you sure you want to remove ${
                        getUserPreferredFullName(accountUser) || 'this member'
                    } from ${course.name}?`,
                    variant: 'destructive',
                    actionButtonText: 'Remove',
                    onAction: () => {
                        message.success(`${getUserPreferredFullName(accountUser) || 'Member'} successfully removed.`);
                        setDialog((current) => ({
                            ...current,
                            open: false,
                            onAction: () => {},
                        }));
                        removeUser(accountUser);
                    },
                }));
                break;
            default:
                break;
        }
    };

    const numFilters = countFilters(memberFilters, sortState);

    const drawerProps = {
        anchor: 'left',
        open: memberFilters.open,
        onClose: () => setMemberFilters((current) => ({ ...current, open: false })),
    };
    const memberFilterProps = { memberFilters, setMemberFilters, sortState, handleSetSortState };
    const responsiveFilterDrawers = (device) => {
        return {
            mobile: (
                <Drawer device={'mobile'} {...drawerProps}>
                    <MemberFilter device={'mobile'} {...memberFilterProps} />
                </Drawer>
            ),
            tablet: (
                <Drawer device={'tablet'} {...drawerProps}>
                    <MemberFilter device={'tablet'} {...memberFilterProps} />
                </Drawer>
            ),
            desktop: (
                <Drawer device={'tablet'} {...drawerProps}>
                    <MemberFilter device={'desktop'} {...memberFilterProps} />
                </Drawer>
            ),
        }[device];
    };

    const handleClickResend = () => {
        setDialog((current) => ({
            ...current,
            open: true,
            message: 'Reinvite Members?',
            description: 'Members who have already accepted an invitation will not be notified.',
            variant: 'primary',
            actionButtonText: 'Resend Invite',
            onAction: () => {
                const toInvite = checked
                    .map((id) => accountUsers.find(({ owner_account_id }) => owner_account_id === id))
                    .filter(({ login_activity }) => !Boolean(login_activity));
                const instructorsToInvite = toInvite.filter((accountUser) => accountUser.role === 'instructor');
                const studentsToInvite = toInvite.filter((accountUser) => accountUser.role === 'student');
                if (instructorsToInvite.length) {
                    sendInvitations('instructor', instructorsToInvite);
                }
                if (studentsToInvite.length) {
                    sendInvitations('student', studentsToInvite);
                }
                if (instructorsToInvite.length + studentsToInvite.length) {
                    message.success(`${checked.length} members successfully reinvited.`);
                }
                setChecked([]);
                setDialog((current) => ({ ...current, open: false, onAction: () => {} }));
            },
        }));
    };

    const handleClickRemove = () => {
        setDialog((current) => ({
            ...current,
            open: true,
            message: `Remove ${checked.length} Members?`,
            description: 'This cannot be undone.',
            variant: 'destructive',
            actionButtonText: 'Remove',
            onAction: () => {
                const toRemove = checked.filter((id) => id !== auth.owner_account_id);
                if (toRemove.length) {
                    toRemove.forEach((id) => removeUser({ owner_account_id: id }));
                    message.success(`${checked.length} member(s) successfully removed.`);
                }
                setChecked([]);
                setDialog((current) => ({ ...current, open: false, onAction: () => {} }));
            },
        }));
    };

    const Title = (
        <Text component={'h1'} variant={'h6'} weight={'semibold'}>
            Members
        </Text>
    );

    const InviteSyncButton =
        process.env.REACT_APP_AUTH === 'PASSWORD' ? (
            <Dropdown
                id={'invite-members-dropdown'}
                options={[
                    { key: 'instructors', value: 'Invite Instructors' },
                    { key: 'students', value: 'Invite Students' },
                ]}
                rootComponent={<Button>Invite</Button>}
                onSelect={handleSelectInvite}
            />
        ) : (
            <Button
                loading={isSyncRosterLoading}
                onClick={() => setSyncRosterDialog({ open: true, isGroupSyncing: false })}
            >
                Sync Roster
            </Button>
        );

    const ResendInvitationsButton =
        process.env.REACT_APP_AUTH !== 'PASSWORD' ? null : device === 'mobile' ? (
            <IconButton
                aria-label={'resend invitation emails'}
                disabled={!Boolean(checked.length)}
                kind={'email'}
                size={'medium'}
                tooltip={{ component: 'Resend Invitations' }}
                variant={'square'}
                onClick={handleClickResend}
            />
        ) : (
            <Button
                disabled={!Boolean(checked.length)}
                startIcon={<Icon kind={'email'} />}
                variant={'tertiary'}
                onClick={handleClickResend}
            >
                Resend Invitations
            </Button>
        );

    const RemoveMembersButton =
        process.env.REACT_APP_AUTH !== 'PASSWORD' ? null : device === 'mobile' ? (
            <IconButton
                aria-label={'remove from course'}
                disabled={!Boolean(checked.length)}
                kind={'delete'}
                size={'medium'}
                tooltip={{ component: 'Remove from course' }}
                variant={'square'}
                onClick={handleClickRemove}
            />
        ) : (
            <Button
                color={'destructive'}
                disabled={!Boolean(checked.length)}
                startIcon={<Icon kind={'delete'} />}
                variant={'tertiary'}
                onClick={handleClickRemove}
            >
                Remove
            </Button>
        );

    const FilterComponents = (
        <>
            <Button
                color={'neutral'}
                startIcon={<Icon kind={'filter'} />}
                variant={'secondary'}
                onClick={() => setMemberFilters((current) => ({ ...current, open: true }))}
            >
                {device === 'mobile' ? `(${numFilters})` : `Filter (${numFilters})`}
            </Button>
            <CustomTextInput
                device={device}
                disablePointerEventsOnIcon={!Boolean(memberFilters.searchInput)}
                endIcon={
                    memberFilters.searchInput ? (
                        <IconButton
                            aria-label={'clear search input'}
                            kind={'exit'}
                            variant={'square'}
                            onClick={() => setMemberFilters((current) => ({ ...current, searchInput: '' }))}
                            onMouseDown={(e) => e.preventDefault()}
                        />
                    ) : (
                        <Icon kind={'search'} />
                    )
                }
                placeholder={'Search'}
                value={memberFilters.searchInput}
                onChange={(e) => setMemberFilters((current) => ({ ...current, searchInput: e.target.value }))}
            />
        </>
    );

    const Header = {
        mobile: (
            <>
                <Stack alignItems={'center'} direction={'row'} justifyContent={'space-between'}>
                    {Title}
                </Stack>
                <Stack direction={'row'} spacing={4}>
                    {FilterComponents}
                    {InviteSyncButton}
                </Stack>
                <Stack
                    sx={{
                        padding: theme.spacing(2),
                        borderBottom: `1px solid ${theme.palette.ensightful.neutral[300]}`,
                    }}
                    alignItems={'center'}
                    direction={'row'}
                    justifyContent={'space-between'}
                >
                    <Stack alignItems={'center'} direction={'row'}>
                        <Checkbox
                            aria-label={'select all members'}
                            checked={accountUsers?.every((accountUser) =>
                                checked.includes(accountUser.owner_account_id)
                            )}
                            onChange={(e) => {
                                if (e.target.checked) {
                                    setChecked(accountUsers?.map(({ owner_account_id }) => owner_account_id));
                                } else {
                                    setChecked([]);
                                }
                            }}
                        />
                        <Text sx={{ flexShrink: 0 }} color={'light'} weight={'medium'}>
                            Name
                        </Text>
                    </Stack>
                    <Stack direction={'row'} spacing={2}>
                        {RemoveMembersButton}
                        {ResendInvitationsButton}
                    </Stack>
                </Stack>
            </>
        ),
        tablet: (
            <>
                <Stack direction={'row'} justifyContent={'space-between'}>
                    {Title}
                    <Stack direction={'row'} spacing={4}>
                        {RemoveMembersButton}
                        {ResendInvitationsButton}
                    </Stack>
                </Stack>
                <Stack sx={{ ml: 'auto', width: 'fit-content' }} direction={'row'} spacing={4}>
                    {FilterComponents}
                    {InviteSyncButton}
                </Stack>
            </>
        ),
        desktop: (
            <>
                <Stack alignItems={'center'} direction={'row'} justifyContent={'space-between'}>
                    {Title}
                    <Stack sx={{ ml: 'auto' }} direction={'row'} spacing={4}>
                        <Button startIcon={<Icon kind={'download'} />} variant={'tertiary'} onClick={downloadRosterCSV}>
                            Download Course Roster
                        </Button>
                        {FilterComponents}
                        {InviteSyncButton}
                    </Stack>
                </Stack>
                <Stack direction={'row-reverse'} spacing={4}>
                    {ResendInvitationsButton}
                    {RemoveMembersButton}
                </Stack>
            </>
        ),
    }[device];

    return (
        <>
            <CustomStack device={device} spacing={4}>
                {Header}
                <MemberList
                    device={device}
                    accountUsers={accountUsers}
                    setAccountUsers={setAccountUsers}
                    sortState={sortState}
                    handleSetSortState={handleSetSortState}
                    checked={checked}
                    setChecked={setChecked}
                    getMemberActionOptions={getMemberActionOptions}
                    onSelect={handleSelectMemberAction}
                />
            </CustomStack>
            {!isLTI && (
                <InstructorInvitations
                    open={isInviteInstructorOpen}
                    setOpen={setIsInviteInstructorOpen}
                    accountUsers={accountUsers}
                    inputValue={inviteInstructorInputValue}
                    setInputValue={setInviteInstructorInputValue}
                    emails={inviteInstructorEmails}
                    setEmails={setInviteInstructorEmails}
                    isLoading={isInviteInstructorLoading}
                    setIsLoading={setIsInviteInstructorLoading}
                    sendInvitations={sendInvitations}
                    getMemberActionOptions={getMemberActionOptions}
                    onSelect={handleSelectMemberAction}
                />
            )}
            {!isLTI && (
                <StudentInvitations
                    open={isInviteStudentOpen}
                    setOpen={setIsInviteStudentOpen}
                    course={course}
                    accountUsers={accountUsers}
                    csvStudents={csvStudents}
                    setCSVStudents={setCSVStudents}
                    accordions={inviteStudentAccordions}
                    setAccordions={setInviteStudentAccordions}
                    inputValue={inviteStudentInputValue}
                    setInputValue={setInviteStudentInputValue}
                    emails={inviteStudentEmails}
                    setEmails={setInviteStudentEmails}
                    isLoading={isInviteStudentLoading}
                    setIsLoading={setIsInviteStudentLoading}
                    sendInvitations={sendInvitations}
                    getMemberActionOptions={getMemberActionOptions}
                    onSelect={handleSelectMemberAction}
                />
            )}
            {responsiveFilterDrawers(device)}
        </>
    );
};

const CustomStack = styled(Stack)(({ device, theme }) => {
    return {
        ...(device === 'mobile' && {
            paddingBottom: theme.spacing(8),
        }),
    };
});

const CustomTextInput = styled(TextInput)(({ device }) => {
    return {
        ...(device === 'mobile' && {
            flex: 1,
            width: 'unset',
        }),
        ...(device === 'tablet' && {
            flex: 1,
            width: 'unset',
        }),
        ...(device === 'desktop' && {
            width: '250px',
        }),
    };
});

export default MembersTab;
